import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ChevronLeft, ChevronRight, FileBarChart, UsersRound, PhoneCall, MapPin, Trophy, Target, Briefcase, LogIn, Store, Database, FileText, KeyRound } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatINRShort, formatDate } from "@/lib/constants";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DSRAnalytics } from "@/components/DSRAnalytics";
import { StatusBadge } from "@/components/StatusBadge";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ROLE_SHORT = { sales_head: "Sales Head", region_head: "Region Head", sales_manager: "Manager", sales_coordinator: "Coordinator", salesperson: "Sales", bd: "BD", sc: "SC" };

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

function AchBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-slate-300">—</span>;
  const cls = pct >= 100 ? "text-emerald-700 bg-emerald-50" : pct >= 60 ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
  return <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${cls}`}>{pct}%</span>;
}

function DrillSection({ title, children, empty }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{title}</p>
      {empty ? <p className="text-sm text-slate-400 py-1">Nothing yet.</p> : children}
    </div>
  );
}

const MO_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const AXIS = { fontSize: 11, fill: "#94a3b8" };
const TIP = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };

function TrendCharts() {
  const { data } = useQuery({
    queryKey: ["team-trends"],
    queryFn: async () => (await api.get("/team/trends", { params: { months: 6 } })).data,
  });
  const rows = (data?.months || []).map((m) => ({ ...m, label: MO_SHORT[parseInt(m.month.slice(5), 10) - 1] }));
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
      <Card className="p-4 shadow-none border-slate-200/80" data-testid="team-trend-won">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Won Value · Last 6 Months</p>
        <ResponsiveContainer width="100%" height={170}>
          <BarChart data={rows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} width={62} tickFormatter={(v) => formatINRShort(v)} />
            <Tooltip contentStyle={TIP} formatter={(v, n) => [n === "Won" ? formatINRShort(v) : v, n]} />
            <Bar dataKey="won_value" name="Won" fill="#0f172a" radius={[4, 4, 0, 0]} maxBarSize={34} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card className="p-4 shadow-none border-slate-200/80" data-testid="team-trend-activity">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Activity · Leads, Calls & Visits</p>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={rows} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
            <Tooltip contentStyle={TIP} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
            <Line type="monotone" dataKey="calls" name="Calls" stroke="#f97316" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="leads" name="Leads" stroke="#0ea5e9" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="visits" name="Visits" stroke="#10b981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

function TeamTab({ monthStr }) {
  const { user, impersonate } = useAuth();
  const canViewAs = ["super_admin", "management", "sales_head"].includes(user?.role);
  const [drill, setDrill] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["team-performance", monthStr],
    queryFn: async () => (await api.get("/team/performance", { params: { month: monthStr } })).data,
  });
  const detail = useQuery({
    queryKey: ["team-member", drill?.id],
    queryFn: async () => (await api.get(`/team/member/${drill.id}`)).data,
    enabled: !!drill,
  });

  const rows = data?.rows || [];
  const roll = data?.rollup || {};
  const regions = [...new Set(rows.map((r) => r.region || "Unassigned"))];
  const dd = detail.data;

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Team" value={roll.members || 0} sub="people under you" icon={UsersRound} testid="team-kpi-members" />
        <Kpi label="Leads (month)" value={roll.leads_month || 0} icon={Briefcase} testid="team-kpi-leads" />
        <Kpi label="Open Pipeline" value={formatINRShort(roll.pipeline_value || 0)} sub={`${roll.pipeline_count || 0} deals`} icon={Target} testid="team-kpi-pipeline" />
        <Kpi label="Won (FY)" value={formatINRShort(roll.won_value || 0)} sub={`${roll.won_count || 0} orders${roll.achieved_pct != null ? ` · ${roll.achieved_pct}% of target` : ""}`} icon={Trophy} testid="team-kpi-won" />
        <Kpi label="Calls (month)" value={roll.calls_month || 0} sub="logged + DSR" icon={PhoneCall} testid="team-kpi-calls" />
        <Kpi label="Site Visits (month)" value={roll.visits_month || 0} icon={MapPin} testid="team-kpi-visits" />
      </div>

      <TrendCharts />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-2.5">Person</th>
                <th className="px-3 py-2.5 text-right">Leads (mo)</th>
                <th className="px-3 py-2.5 text-right">Pipeline</th>
                <th className="px-3 py-2.5 text-right">Won FY</th>
                <th className="px-3 py-2.5 text-right">Lost FY</th>
                <th className="px-3 py-2.5 text-right">Win %</th>
                <th className="px-3 py-2.5 text-right">Calls (mo)</th>
                <th className="px-3 py-2.5 text-right">Visits (mo)</th>
                <th className="px-3 py-2.5 text-right">Target FY</th>
                <th className="px-3 py-2.5 text-right">Ach.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-slate-400">No team members found under you.</td></tr>}
              {regions.map((reg) => (
                [
                  <tr key={`h-${reg}`} className="bg-slate-100/70">
                    <td colSpan={10} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{reg}</td>
                  </tr>,
                  ...rows.filter((r) => (r.region || "Unassigned") === reg).map((r) => (
                    <tr key={r.id} className="hover:bg-orange-50/40 cursor-pointer transition-colors" onClick={() => setDrill(r)} data-testid={`team-row-${r.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-800">{r.name}</p>
                        <p className="text-[11px] text-slate-400">{ROLE_SHORT[r.role] || r.role}{r.manager ? ` · reports to ${r.manager}` : ""}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.leads_month}<span className="text-slate-300 text-xs"> /{r.leads_total}</span></td>
                      <td className="px-3 py-2.5 text-right font-mono">{formatINRShort(r.pipeline_value)}<span className="text-slate-400 text-xs"> ({r.pipeline_count})</span></td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{formatINRShort(r.won_value)}<span className="text-slate-400 text-xs"> ({r.won_count})</span></td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.lost_count || <span className="text-slate-300">0</span>}</td>
                      <td className="px-3 py-2.5 text-right">{r.win_rate != null ? `${r.win_rate}%` : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.calls_month}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.visits_month}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.target_fy ? formatINRShort(r.target_fy) : <span className="text-slate-300">—</span>}</td>
                      <td className="px-3 py-2.5 text-right"><AchBadge pct={r.achieved_pct} /></td>
                    </tr>
                  )),
                ]
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 px-4 py-2 border-t border-slate-100">Leads shows this month / all-time · Won, Lost, Win % and Target are for the financial year · Calls = call logs + manual DSR entries for the selected month · Click a person for their pipeline, leads, visits & call logs.</p>
      </Card>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="member-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">{drill?.name}
              <span className="text-sm font-normal text-slate-400">{ROLE_SHORT[drill?.role] || drill?.role} · {drill?.region || "—"}</span>
              {canViewAs && (
                <Button variant="outline" size="sm" className="ml-auto gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  onClick={async () => { const r = await impersonate(drill.id); if (!r.ok) toast.error(r.error); }}
                  data-testid="team-view-as-btn">
                  <LogIn className="h-3.5 w-3.5" /> View their account
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail.isLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
          ) : (
            <div className="space-y-5">
              <DrillSection title={`Open Opportunities (${dd?.open_opportunities?.length || 0})`} empty={!dd?.open_opportunities?.length}>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {dd?.open_opportunities?.map((o) => (
                        <tr key={o.id}>
                          <td className="px-3 py-2 font-medium text-slate-800">{o.name}<p className="text-[11px] text-slate-400 font-normal">{o.company}</p></td>
                          <td className="px-3 py-2 text-slate-600">{o.stage}</td>
                          <td className="px-3 py-2">{o.sub_rating ? <StatusBadge kind="rating" value={o.sub_rating} /> : <span className="text-slate-300">—</span>}</td>
                          <td className="px-3 py-2 text-right font-mono">{formatINRShort(o.deal_value)}</td>
                          <td className="px-3 py-2 text-right text-slate-500 text-xs">{o.expected_close_date ? formatDate(o.expected_close_date) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DrillSection>
              <DrillSection title="Recent Leads" empty={!dd?.recent_leads?.length}>
                <div className="space-y-1">
                  {dd?.recent_leads?.map((l) => (
                    <p key={l.id} className="text-sm text-slate-700 flex items-center gap-2">
                      <span className="font-medium">{l.name}</span>
                      <span className="text-slate-400 text-xs">{l.company}</span>
                      <span className="text-xs text-slate-500">· {l.lead_status}</span>
                      <span className="text-[11px] text-slate-400 ml-auto">{formatDate(l.created_at)}</span>
                    </p>
                  ))}
                </div>
              </DrillSection>
              <DrillSection title="Recent Site Visits" empty={!dd?.recent_visits?.length}>
                <div className="space-y-1">
                  {dd?.recent_visits?.map((v) => (
                    <p key={v.id} className="text-sm text-slate-700 flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                      <span>{v.address || v.notes || v.entity_type}<span className="text-[11px] text-slate-400 ml-2">{formatDate(v.visit_date)}</span></span>
                    </p>
                  ))}
                </div>
              </DrillSection>
              <DrillSection title="Recent Call Logs" empty={!dd?.recent_calls?.length}>
                <div className="space-y-1.5">
                  {dd?.recent_calls?.map((c) => (
                    <div key={c.id} className="text-sm">
                      <p className="text-slate-700"><span className="font-medium">{c.company || c.contact_name || "Call"}</span><span className="text-[11px] text-slate-400 ml-2">{formatDate(c.call_at)}</span></p>
                      {c.summary && <p className="text-xs text-slate-500 line-clamp-2">{c.summary}</p>}
                    </div>
                  ))}
                </div>
              </DrillSection>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DealersTab() {
  const { user, impersonate } = useAuth();
  const canViewAs = ["super_admin", "management", "sales_head"].includes(user?.role);
  const [drill, setDrill] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["team-dealers"],
    queryFn: async () => (await api.get("/team/dealers")).data,
  });
  const detail = useQuery({
    queryKey: ["team-dealer", drill?.login_user_id],
    queryFn: async () => (await api.get(`/team/dealer/${drill.login_user_id}`)).data,
    enabled: !!drill?.login_user_id,
  });

  const rows = data?.rows || [];
  const zones = [...new Set(rows.map((r) => r.zone || "Unassigned"))];
  const dd = detail.data;
  const pipelineTotal = rows.reduce((s, r) => s + (r.pipeline_value || 0), 0);
  const wonTotal = rows.reduce((s, r) => s + (r.won_value || 0), 0);
  const universeTotal = rows.reduce((s, r) => s + (r.universe || 0), 0);

  const openDrill = (r) => {
    if (!r.login_user_id) { toast.info("This dealer has no portal login yet — add one in Utility → Dealer Assignment."); return; }
    setDrill(r);
  };

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Dealers" value={data?.total || 0} sub={`${rows.filter((r) => r.has_login).length} with portal login`} icon={Store} testid="dealers-kpi-count" />
        <Kpi label="Open Pipeline" value={formatINRShort(pipelineTotal)} sub={`${rows.reduce((s, r) => s + (r.pipeline_count || 0), 0)} deals`} icon={Target} testid="dealers-kpi-pipeline" />
        <Kpi label="Won" value={formatINRShort(wonTotal)} sub={`${rows.reduce((s, r) => s + (r.won_count || 0), 0)} orders`} icon={Trophy} testid="dealers-kpi-won" />
        <Kpi label="Universe Accounts" value={universeTotal} sub="dealer-created companies" icon={Database} testid="dealers-kpi-universe" />
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-2.5">Dealer</th>
                <th className="px-3 py-2.5">Tagged Salesperson</th>
                <th className="px-3 py-2.5 text-center">Portal Login</th>
                <th className="px-3 py-2.5 text-right">Leads</th>
                <th className="px-3 py-2.5 text-right">Pipeline</th>
                <th className="px-3 py-2.5 text-right">Won</th>
                <th className="px-3 py-2.5 text-right">Universe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400">No dealers tagged to you yet.</td></tr>}
              {zones.map((zone) => (
                [
                  <tr key={`z-${zone}`} className="bg-slate-100/70">
                    <td colSpan={7} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{zone}</td>
                  </tr>,
                  ...rows.filter((r) => (r.zone || "Unassigned") === zone).map((r) => (
                    <tr key={r.id} className={`transition-colors ${r.login_user_id ? "hover:bg-orange-50/40 cursor-pointer" : "opacity-60"}`} onClick={() => openDrill(r)} data-testid={`dealer-row-${r.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-800 flex items-center gap-1.5"><Store className="h-3.5 w-3.5 text-slate-400 shrink-0" />{r.name}</p>
                        <p className="text-[11px] text-slate-400 pl-5">{r.city || "—"}</p>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{r.salesperson_name}</td>
                      <td className="px-3 py-2.5 text-center">
                        {r.has_login
                          ? <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold text-emerald-700 bg-emerald-50">Active</span>
                          : <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold text-slate-500 bg-slate-100">None</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.leads}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{formatINRShort(r.pipeline_value)}<span className="text-slate-400 text-xs"> ({r.pipeline_count})</span></td>
                      <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{formatINRShort(r.won_value)}<span className="text-slate-400 text-xs"> ({r.won_count})</span></td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.universe}</td>
                    </tr>
                  )),
                ]
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 px-4 py-2 border-t border-slate-100">Numbers reflect each dealer's own private portal data (their leads, opportunities, universe & quotes) · Click a dealer to drill into their accounts{canViewAs ? " or open their portal in view-as mode" : ""}.</p>
      </Card>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dealer-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
              <Store className="h-4 w-4 text-brand shrink-0" />{drill?.name}
              <span className="text-sm font-normal text-slate-400">Dealer · {drill?.zone || "—"}{drill?.city ? ` · ${drill.city}` : ""}</span>
              {canViewAs && drill?.has_login && (
                <Button variant="outline" size="sm" className="ml-auto gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  onClick={async () => { const r = await impersonate(drill.login_user_id); if (!r.ok) toast.error(r.error); }}
                  data-testid="dealer-view-as-btn">
                  <LogIn className="h-3.5 w-3.5" /> View their account
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {detail.isLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
          ) : detail.isError ? (
            <p className="text-sm text-red-600 py-4" data-testid="dealer-drill-error">{apiError(detail.error)}</p>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500" data-testid="dealer-drill-info">
                {dd?.dealer?.email && <span className="flex items-center gap-1"><KeyRound className="h-3 w-3" />{dd.dealer.email}</span>}
                {dd?.dealer?.tagged_salesperson_name && <span className="flex items-center gap-1"><UsersRound className="h-3 w-3" />Tagged to {dd.dealer.tagged_salesperson_name}</span>}
              </div>
              <DrillSection title={`Open Opportunities (${dd?.open_opportunities?.length || 0})`} empty={!dd?.open_opportunities?.length}>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {dd?.open_opportunities?.map((o) => (
                        <tr key={o.id}>
                          <td className="px-3 py-2 font-medium text-slate-800">{o.name}<p className="text-[11px] text-slate-400 font-normal">{o.company}</p></td>
                          <td className="px-3 py-2 text-slate-600">{o.stage}</td>
                          <td className="px-3 py-2">{o.sub_rating ? <StatusBadge kind="rating" value={o.sub_rating} /> : <span className="text-slate-300">—</span>}</td>
                          <td className="px-3 py-2 text-right font-mono">{formatINRShort(o.deal_value)}</td>
                          <td className="px-3 py-2 text-right text-slate-500 text-xs">{o.expected_close_date ? formatDate(o.expected_close_date) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DrillSection>
              <DrillSection title="Recent Leads" empty={!dd?.recent_leads?.length}>
                <div className="space-y-1">
                  {dd?.recent_leads?.map((l) => (
                    <p key={l.id} className="text-sm text-slate-700 flex items-center gap-2">
                      <span className="font-medium">{l.name}</span>
                      <span className="text-slate-400 text-xs">{l.company}</span>
                      <span className="text-xs text-slate-500">· {l.lead_status}</span>
                      <span className="text-[11px] text-slate-400 ml-auto">{formatDate(l.created_at)}</span>
                    </p>
                  ))}
                </div>
              </DrillSection>
              <DrillSection title={`Their Universe (${dd?.universe?.length || 0})`} empty={!dd?.universe?.length}>
                <div className="flex flex-wrap gap-1.5">
                  {dd?.universe?.map((u) => (
                    <span key={u.id} className="px-2 py-1 rounded bg-slate-100 text-xs text-slate-700" data-testid={`dealer-universe-${u.id}`}>
                      {u.company}{u.category ? <span className="text-slate-400"> · {u.category}</span> : null}
                    </span>
                  ))}
                </div>
              </DrillSection>
              <DrillSection title="Recent Quotes" empty={!dd?.quotes?.length}>
                <div className="space-y-1">
                  {dd?.quotes?.map((q) => (
                    <p key={q.id} className="text-sm text-slate-700 flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="font-medium">{q.quote_number || q.deal_name || "Quote"}</span>
                      <span className="text-slate-400 text-xs">{q.company}</span>
                      <span className="text-[11px] font-mono text-slate-500 ml-auto">{formatINRShort(q.total_amount)}</span>
                    </p>
                  ))}
                </div>
              </DrillSection>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MyTeam() {
  const now = new Date();
  const { user } = useAuth();
  const isRep = user?.role === "salesperson";
  const [tab, setTab] = useState(isRep ? "dealers" : "team");
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [dsrOpen, setDsrOpen] = useState(false);
  const monthStr = `${ym.y}-${String(ym.m).padStart(2, "0")}`;
  const shift = (d) => setYm(({ y, m }) => { const t = new Date(y, m - 1 + d, 1); return { y: t.getFullYear(), m: t.getMonth() + 1 }; });

  return (
    <div data-testid="my-team-page">
      <PageHeader title="Business Insights" subtitle={isRep ? "Your dealer partners and how they're doing" : "Your people and dealer partners at a glance"}>
        {tab === "team" && !isRep && (
          <>
            <div className="flex items-center gap-1 border border-slate-200 rounded-md bg-white px-1 h-9">
              <button onClick={() => shift(-1)} className="p-1 text-slate-400 hover:text-slate-900" data-testid="team-month-prev"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-sm font-medium text-slate-700 min-w-[112px] text-center" data-testid="team-month-label">{MONTHS[ym.m - 1]} {ym.y}</span>
              <button onClick={() => shift(1)} className="p-1 text-slate-400 hover:text-slate-900" data-testid="team-month-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <Button variant="outline" className="gap-1.5" onClick={() => setDsrOpen(true)} data-testid="team-dsr-btn"><FileBarChart className="h-4 w-4" /> DSR Report</Button>
          </>
        )}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        {!isRep && (
          <TabsList className="mb-4">
            <TabsTrigger value="team" className="gap-1.5" data-testid="team-tab-team"><UsersRound className="h-3.5 w-3.5" /> Team</TabsTrigger>
            <TabsTrigger value="dealers" className="gap-1.5" data-testid="team-tab-dealers"><Store className="h-3.5 w-3.5" /> Dealers</TabsTrigger>
          </TabsList>
        )}
        {!isRep && <TabsContent value="team"><TeamTab monthStr={monthStr} /></TabsContent>}
        <TabsContent value="dealers"><DealersTab /></TabsContent>
      </Tabs>

      <Dialog open={dsrOpen} onOpenChange={setDsrOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="team-dsr-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileBarChart className="h-5 w-5 text-brand" /> DSR — Daily Calls Report</DialogTitle></DialogHeader>
          <DSRAnalytics />
        </DialogContent>
      </Dialog>
    </div>
  );
}
